import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";

const API_URL = `${getAPIbaseURL()}/time-distribution`;

const getCurrentMonthKey = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export const fetchTimeDistributionData = createAsyncThunk(
  "timeDistribution/fetchData",
  async (params, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || i18n.t("slices.timeDistribution.errors.fetch")
      );
    }
  }
);

export const generateTimeDistribution = createAsyncThunk(
  "timeDistribution/generate",
  async ({ month }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/generate`,
        { month },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(i18n.t("slices.timeDistribution.notifications.generated"));
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.timeDistribution.errors.generate");
      toast.error(message);
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

export const createAllocation = createAsyncThunk(
  "timeDistribution/createAllocation",
  async (allocationData, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(API_URL, allocationData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(i18n.t("slices.timeDistribution.notifications.created"));
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.timeDistribution.errors.create");
      toast.error(message);
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

export const updateAllocation = createAsyncThunk(
  "timeDistribution/updateAllocation",
  async ({ allocationId, data }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/${allocationId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(i18n.t("slices.timeDistribution.notifications.updated"));
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.timeDistribution.errors.update");
      toast.error(message);
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

export const deleteAllocation = createAsyncThunk(
  "timeDistribution/deleteAllocation",
  async (allocationId, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/${allocationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(i18n.t("slices.timeDistribution.notifications.deleted"));
      return allocationId;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.timeDistribution.errors.delete");
      toast.error(message);
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

export const fetchWorkPackagesForProject = createAsyncThunk(
  "timeDistribution/fetchWorkPackagesForProject",
  async ({ projectId, month }, { getState, rejectWithValue }) => {
    try {
      const { timeDistribution } = getState();
      const cacheKey = `${month || "current"}:${projectId}`;
      if (timeDistribution.workPackagesByProject[cacheKey]) {
        return { projectId, cacheKey, data: timeDistribution.workPackagesByProject[cacheKey] };
      }

      const token = localStorage.getItem("token");
      const response = await axios.get(`${getAPIbaseURL()}/work-packages/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: month ? { month } : undefined,
      });
      return { projectId, cacheKey, data: response.data };
    } catch (error) {
      return rejectWithValue({
        projectId,
        error: error.response?.data?.detail || i18n.t("slices.timeDistribution.errors.fetch_wps")
      });
    }
  }
);

export const updateWorkPackageMember = createAsyncThunk(
  "timeDistribution/updateWorkPackageMember",
  async ({ wpId, userId, etis, month }, thunkAPI) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${getAPIbaseURL()}/time-distribution/work-packages/${wpId}/members/${userId}`,
        { etis, ...(month ? { month } : {}) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || i18n.t("slices.timeDistribution.errors.update");
      return thunkAPI.rejectWithValue(error.response?.data || message);
    }
  }
);

export const fetchUserAllocations = createAsyncThunk(
  "timeDistribution/fetchUserAllocations",
  async (request, thunkAPI) => {
    try {
      const { userId, month } = typeof request === "string" ? { userId: request } : request;
      const token = localStorage.getItem("token");
      const response = await axios.get(`${getAPIbaseURL()}/time-distribution/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: month ? { month } : undefined,
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.detail || i18n.t("slices.timeDistribution.errors.fetch")
      );
    }
  }
);

const timeDistributionSlice = createSlice({
  name: "timeDistribution",
  initialState: {
    overview: {
      activeProjectsCount: 0,
      totalEmployeesCount: 0,
      globalEti: "0.00 / 0.00",
      conflictsCount: 0,
    },
    allocations: [],
    directory: [],
    workPackagesByProject: {},
    loadingPackagesFor: [],
    distributionExists: null,
    distributionMonth: null,
    distributionStatus: null,
    isLoading: false,
    error: null,
    message: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTimeDistributionData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.allocations = [];
        state.directory = [];
        state.workPackagesByProject = {};
        state.distributionExists = null;
        state.distributionStatus = null;
      })
      .addCase(fetchTimeDistributionData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.overview = action.payload.overview || state.overview;
        state.allocations = action.payload.allocations || [];
        state.directory = action.payload.directory || [];
        state.distributionMonth = action.meta.arg?.month || null;
        state.distributionExists = typeof action.payload.distribution_exists === "boolean"
          ? action.payload.distribution_exists
          : !action.meta.arg?.month || action.meta.arg.month === getCurrentMonthKey();
        state.distributionStatus = action.payload.status || null;
      })
      .addCase(generateTimeDistribution.fulfilled, (state) => {
        state.isLoading = false;
        state.distributionExists = true;
        state.distributionStatus = "generated";
      })
      .addCase(createAllocation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allocations.push(action.payload);
        state.message = i18n.t("slices.timeDistribution.notifications.created");
      })
      .addCase(updateAllocation.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;

        state.allocations.forEach((project) => {
          if (project.id === payload.project_id) {
            project.allocated_eti = payload.project_allocated_eti;
            project.status = payload.project_status;
          }

          project.resources?.forEach((resource) => {
            if (resource.id === payload.user_id) {
              resource.total_eti = payload.user_total_eti;
              resource.has_conflict = payload.has_conflict;
            }
            if (resource.allocation_id === payload.allocation_id) {
              resource.allocated = payload.monthly_etis;
            }
          });
        });

        state.message = i18n.t("slices.timeDistribution.notifications.updated");
      })
      .addCase(deleteAllocation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allocations = state.allocations.filter((item) => item.id !== action.payload);
        state.message = i18n.t("slices.timeDistribution.notifications.deleted");
      })
      .addCase(fetchWorkPackagesForProject.pending, (state, action) => {
        const { projectId, month } = action.meta.arg;
        const cacheKey = `${month || "current"}:${projectId}`;
        if (!state.loadingPackagesFor.includes(cacheKey)) {
          state.loadingPackagesFor.push(cacheKey);
        }
      })
      .addCase(fetchWorkPackagesForProject.fulfilled, (state, action) => {
        const { cacheKey, data } = action.payload;
        state.workPackagesByProject[cacheKey] = data;
        state.loadingPackagesFor = state.loadingPackagesFor.filter((id) => id !== cacheKey);
      })
      .addCase(fetchWorkPackagesForProject.rejected, (state, action) => {
        const { projectId, month } = action.meta.arg;
        const cacheKey = `${month || "current"}:${projectId}`;
        state.workPackagesByProject[cacheKey] = [];
        state.loadingPackagesFor = state.loadingPackagesFor.filter((id) => id !== cacheKey);
      })
      .addMatcher(
        (action) => 
          action.type.startsWith("timeDistribution/") && 
          action.type.endsWith("/pending") &&
          !action.type.includes("fetchWorkPackagesForProject"),
        (state) => {
          state.isLoading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => 
          action.type.startsWith("timeDistribution/") && 
          action.type.endsWith("/rejected") &&
          !action.type.includes("fetchWorkPackagesForProject"),
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { clearError, clearMessage } = timeDistributionSlice.actions;
export default timeDistributionSlice.reducer;
